import * as React from "react";

import {
  EmployeeDto,
  TicketStatus,
  TicketType,
  getUsersGetLoggedUserInfo,
} from "Api/Api";
import { Resources, useResource } from "Translations/Resources";
import {
  changeTicketStatusAsync,
  closeTicketCancel,
  getTicketsAsync,
  hideTicket,
  openTicketCancel,
} from "State/Tickets/TicketReducer";
import { useDispatch, useSelector } from "react-redux";

import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import Cookies from "universal-cookie";
import { RecentActivities } from "Components/Dashboard/RecentActivities/RecentActivities";
import { ReminderModal } from "Components/Dashboard/ReminderModal";
import { RootStateType } from "State/Store";
import { SubordinatesTicketList } from "./SubordinatesTicketList";
import { TicketCancelComponent } from "./TicketCancelComponent";
import { TicketList } from "Components/Dashboard/TicketList";
import { getModifiedFirstName } from "Utils/StringUtils";
import styled from "styled-components";

const StyledWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(400px, 600px) minmax(400px, 600px);
  gap: ${props => props.theme.spacing(7, 9)};
  padding-top: ${props => props.theme.spacing(4)}px;

  @media (max-width: 1366px) {
    gap: ${props => props.theme.spacing(7, 5)};
  }

  @media (max-width: 965px) {
    grid-template-columns: minmax(400px, 600px);
    justify-content: center;
  }

  ${props => props.theme.breakpoints.down("xs")} {
    grid-template-columns: 1fr;
    padding-top: 0;
    gap: ${props => props.theme.spacing(3, 5)};
  }
`;

const StyledTicketListConainer = styled.div`
  max-height: 800px;
  overflow-y: auto;
  padding-right: 5px;
`;

export const DashboardPage: React.FunctionComponent = _ => {
  const cookieName = "hideReminder";
  const { t } = useResource();

  const state = useSelector((e: RootStateType) => e.tickets);
  const { user } = useSelector((e: RootStateType) => e.auth);
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    getUsersGetLoggedUserInfo().then(result => {
      setOpen(showReminder(result.json));
    });
  }, []);

  React.useEffect(() => {
    dispatch(getTicketsAsync.request());
  }, [dispatch]);

  const onResolveClick = (ticketID: number, groupCode?: string | null) => {
    if (!state.isProcessingStatusChange) {
      const ticket = state.tickets.find(e => e.ticketID === ticketID);
      if (ticket?.isStatusChangePerformedByExternalSystem) {
        dispatch(hideTicket(ticketID));
      } else {
        dispatch(
          changeTicketStatusAsync.request({
            ticketID: ticketID,
            status: TicketStatus.Resolved,
            changeReason: "",
            groupCode: groupCode,
          }),
        );
      }
    }
  };

  const onCancelClick = (ticketID: number, groupCode?: string | null) => {
    dispatch(openTicketCancel({ ticketID: ticketID, groupCode: groupCode }));
  };

  const onCancelClose = () => {
    dispatch(closeTicketCancel());
  };

  const onCancelConfirm = (changeReason: string) => {
    if (state.openedCancellation) {
      dispatch(
        changeTicketStatusAsync.request({
          ticketID: state.openedCancellation.ticketID,
          status: TicketStatus.Canceled,
          changeReason: changeReason,
          groupCode: state.openedCancellation.groupCode,
        }),
      );
    }
  };

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Dashboard.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  const canShowReminder = () => {
    const hideReminder =
      new Cookies().get<string>(cookieName) === "true" || false;
    return !hideReminder;
  };

  const showReminder = (loggedUserInfo: EmployeeDto) =>
    (loggedUserInfo && loggedUserInfo.showReminder) || false;

  const handleClose = () => {
    setOpen(false);
    new Cookies().set(cookieName, true, {
      path: "/",
      domain: process.env.REACT_APP_DOMAIN,
      expires: new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate() + 5,
      ),
    });
  };

  return (
    <AuthenticatedLayout
      title={`${t(Resources.Dashboard.Title)} ${getModifiedFirstName(user)}`}
      breadcrumbItems={getBreadcrumbItems()}
      isDashboard
      rightPanel={{
        open: true,
        setOpen: () => {},
        width: 320,
        hideDown: 1366,
        getBody: () => <RecentActivities />,
      }}
    >
      <StyledWrapper>
        {!state.isLoading && open && canShowReminder() && (
          <ReminderModal
            open={open}
            onClose={handleClose}
            name={getModifiedFirstName(user)}
          />
        )}
        {!state.isLoading && state.tickets && state.tickets.filter && (
          <>
            <StyledTicketListConainer>
              <TicketList
                tickets={state.tickets.filter(
                  e => e.type === TicketType.Warning || e.type === TicketType.CustomActions,
                )}
                maxItems={2000}
                onResolveClick={onResolveClick}
                onCancelClick={onCancelClick}
                title={t(Resources.Dashboard.Tickets.Warnings)}
              ></TicketList>
            </StyledTicketListConainer>
            <StyledTicketListConainer>
              <SubordinatesTicketList
                subordinates={state.subordinatesTickets}
              ></SubordinatesTicketList>
            </StyledTicketListConainer>
          </>
        )}
      </StyledWrapper>
      {state.openedCancellation && (
        <TicketCancelComponent
          ticketID={state.openedCancellation.ticketID}
          onCancel={onCancelClose}
          onConfirm={onCancelConfirm}
        ></TicketCancelComponent>
      )}
    </AuthenticatedLayout>
  );
};
